import styled from 'styled-components';

import { Separator } from '@components/separator';
import { Text } from '@components/text';
import { TitleDescription, TitleDescriptionProps } from '@components/title-description';
import { Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { Transient } from '@utils/types';

export type MenuItem = TitleDescriptionProps & {
  onSelect: () => void;
  isActive?: boolean;
  skipParentOnSelect?: boolean;
  isSeparator?: boolean;
  isDisabled?: boolean;
};

export type MenuListProps = { items: MenuItem[]; titleFont?: Font; hasPadding?: boolean };

export const MenuList = ({ items, titleFont, hasPadding, ...rest }: MenuListProps) => (
  <Container $hasPadding={hasPadding} {...rest}>
    {items.map(
      ({ onSelect, skipParentOnSelect, isActive, isSeparator, isDisabled, ...rest }, idx) => {
        if (isSeparator) {
          return <Separator key={idx} margin={'0.5rem 0'} />;
        }
        return (
          <ItemContainer
            key={`${rest.title}-${rest.description}-${idx}`}
            onClick={onSelect}
            $isActive={isActive}
            $isDisabled={isDisabled}
            data-cy={`data-cy-menu-list-${rest.title}`}
          >
            <TitleDescription {...rest} titleFont={titleFont} space={2} />
          </ItemContainer>
        );
      },
    )}
  </Container>
);

const activeBackgroundColor = theme.color.uiTrans5;
const activeColor = theme.color.elementBlue100;

const Container = styled.div<Transient<MenuListProps, 'hasPadding'>>(({ $hasPadding }) => ({
  padding: $hasPadding ? pxToRem(16) : undefined,
  overflowY: 'auto',
  flex: 1,
}));

const ItemContainer = styled.div<{ $isActive?: boolean; $isDisabled?: boolean }>(
  ({ $isActive, $isDisabled }) => ({
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    borderRadius: pxToRem(4),
    display: 'flex',
    flex: 1,
    flexDirection: 'column',

    ...($isActive && {
      backgroundColor: activeBackgroundColor,
      '*': {
        color: activeColor,
      },
    }),

    ...(!$isDisabled
      ? {
          cursor: 'pointer',
          ':hover': {
            backgroundColor: activeBackgroundColor,
          },
        }
      : {
          pointerEvents: 'none',
          opacity: 0.5,
        }),
  }),
);

type MenuListHeaderProps = {
  title: string;
  actionButton?: React.ReactNode;
};

export const MenuListHeader = ({ actionButton, title }: MenuListHeaderProps) => {
  const hasActionButton = Boolean(actionButton);

  return (
    <Header $hasActionButton={hasActionButton}>
      {hasActionButton && <ActionButtonContainer>{actionButton}</ActionButtonContainer>}
      <Title text={title} />
    </Header>
  );
};

const Header = styled.div<{ $hasActionButton?: boolean }>(({ $hasActionButton }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: $hasActionButton
    ? `${pxToRem(0)} ${pxToRem(10)} ${pxToRem(8)} ${pxToRem(0)}`
    : `${pxToRem(10)} ${pxToRem(10)} ${pxToRem(18)} ${pxToRem(10)}`,
  marginBottom: pxToRem(8),
  borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
}));

const ActionButtonContainer = styled.div({
  marginRight: pxToRem(8),
});

const Title = styled(Text).attrs({ font: 'bold14', caps: true })({});
